import { useRef } from 'react';
import { colors } from '@federa/components/css-refs.colors';
import { divider } from '@federa/components/css-refs.layout';
import { typography } from '@federa/components/css-refs.typography';
import { spacing } from '@federa/components/css-refs.spacing';
import { opacity, borderRadius } from '@federa/components/css-refs.effects';
import { formatFileSize } from '@federa/utils/formatters/bytes';
import { LAYOUT_CLASSES, MUTED_LABEL_SECONDARY } from '@federa/components/common/layout-styles';
import { Textarea } from '@federa/components/inputs/Textarea';
import {
	BORDER_MUTED,
	FLEX_COLUMN_GAP2_STYLE,
	FLEX_ROW_CENTER_GAP3_STYLE,
	TEXT_SM_MEDIUM,
} from '@federa/components/common/style-primitives';
import { SemanticIcon } from '@federa/components/icons/SemanticIcon';
import { ButtonWithIcon } from '@federa/components/primitives/ButtonWithIcon';
import { CloseButton } from '@federa/components/primitives/CloseButton';
import { loadingMessages } from '@federa/utils/styles/content-tokens';
import { buildSymmetricPadding } from '@federa/utils/styles/style-padding-helpers';
import { ARIA_LIVE, ARIA_ROLES } from '@federa/components/common/aria-tokens';
import { countNoun } from '@federa/const/count-noun';
import { IMAGE_MEDIA_TYPE_PREFIX } from '@federa/const/files';
import { FLEX_ROW_CENTER_STYLE } from '@federa/const/style-flex-presets';
import { TEXT_TRUNCATE_STYLE } from '@federa/const/style-text-presets';
import { UploadProgressBar } from './AssistantPane.components.conversation.tsx';
import type { CSSProperties } from 'react';
import type { SelectedFile } from '@federa/types/ui-chat-messaging';

// Attachment picker config
const MAX_ATTACHMENT_FILES = 5;
const MAX_ATTACHMENT_MB = 10;
const ATTACHMENT_ACCEPT = 'image/jpeg,image/png,image/gif,image/webp';
/** ONE affordance named three times: the hidden file input's accessible name, and the visible proxy button's
 *  accessible name and tooltip. A screen-reader name that drifts from the tooltip is silent to everyone not using one. */
const ATTACH_FILES_LABEL = 'Attach files';

const LOADING_TEXT = loadingMessages.default;

/** Focus ring: brand border while focused, muted at rest -- the focus/blur twin of the hover delta factory. */
const SEND_ERROR_BANNER: CSSProperties = {
	marginBottom: spacing[3],
	paddingLeft: spacing[3],
	paddingRight: spacing[3],
	paddingTop: spacing[2],
	paddingBottom: spacing[2],
	borderRadius: borderRadius.lg,
	backgroundColor: colors.semantic.errorBackground,
	border: `${divider.thickness.thin} solid ${colors.error.DEFAULT}`,
};
const SEND_ERROR_TEXT: CSSProperties = {
	...TEXT_SM_MEDIUM,
	color: colors.error.DEFAULT,
};
/** The banner-local delta over `CloseButton`: the dismissal takes the banner's error colour instead of the neutral one.
 *  Box metrics are the primitive's -- a fixed 28px target -- so nothing here restates padding or type size. */
const SEND_ERROR_DISMISS_BUTTON: CSSProperties = {
	color: colors.error.DEFAULT,
};
/** The composer's own geometry, and all that survives the 2026-08-22 SDL substitution: a single-line-tall box that
 *  centres its text in the send row. Radius, padding, type scale, border colour and surface are `Textarea`'s. */
const MESSAGE_TEXTAREA: CSSProperties = {
	// INLINE for the reason ExploreSearch's TEXTAREA_STYLE records: the `resize-none` class on the element below is
	// overridden by `Textarea`'s own inline `resize: 'vertical'`, so the composer grew a resize grip. The primitive
	// spreads `customStyle` last, so this wins.
	resize: 'none',
	// `border-box` IS THE HEIGHT FIX, and it is needed because nothing else sets it. A `<textarea>` defaults to
	// `content-box`, and `Textarea` applies `padding: <md>` of its own -- so `height` below was the CONTENT height and
	// the padding plus borders were ADDED to it, rendering the composer roughly three times the send button's height.
	// On main this surface was a NATIVE textarea carrying this same `height` and NO padding, which is why the number
	// looked right there and wrong after the SDL adoption (6da8e66f05c). Stating the box model is what makes `height`
	// mean the rendered height again, so the composer lines up with the send control beside it.
	boxSizing: 'border-box',
	// Tighter than the primitive's md default so the single line of text still centres inside that height rather than
	// being clipped by its own padding once the box is bounded.
	padding: `${spacing[2]} ${spacing[3]}`,
	height: spacing[11],
	...FLEX_ROW_CENTER_STYLE,
};
const INPUT_HINT_STYLE: CSSProperties = {
	...MUTED_LABEL_SECONDARY,
	opacity: opacity.strong,
	marginTop: spacing[2],
};
const INPUT_AREA_CONTAINER: CSSProperties = {
	...buildSymmetricPadding(4, 4),
	borderColor: colors.border.muted,
	// The composer is fixed chrome and must NEVER shrink. It is the last child of a flex column whose message list is
	// `flex: 1 1 0%`, and negative free space is distributed by (shrink times base size), so the composer would absorb ALL of it.
	flexShrink: 0,
};
const INPUT_ROW_GAP: CSSProperties = { gap: spacing[2] };

/** The composer-local delta over a ghost `ButtonWithIcon`: the square icon box the composer row is built around (an
 *  icon-only Button pads to zero and rounds to a pill) and the secondary ink the attach affordance uses. Background,
 *  cursor and the disabled treatment are the variant's. */
const ATTACH_BUTTON_STYLE: CSSProperties = {
	padding: spacing[2],
	borderRadius: borderRadius.lg,
	color: colors.text.secondary,
	lineHeight: typography.lineHeight.none,
};
const FILE_PREVIEW_LIST_STYLE: CSSProperties = {
	marginBottom: spacing[3],
	...FLEX_COLUMN_GAP2_STYLE,
};
const FILE_PREVIEW_ROW_STYLE: CSSProperties = {
	...FLEX_ROW_CENTER_GAP3_STYLE,
	padding: `${spacing[2]} ${spacing[3]}`,
	borderRadius: borderRadius.lg,
	border: BORDER_MUTED,
	backgroundColor: colors.background.coolNeutral,
};
const FILE_PREVIEW_NAME_STYLE: CSSProperties = {
	...TEXT_SM_MEDIUM,
	color: colors.text.primary,
	...TEXT_TRUNCATE_STYLE,
};
const FILE_PREVIEW_META_STYLE: CSSProperties = {
	fontSize: typography.fontSize.xs,
	color: colors.text.secondary,
};
/** The row-local delta over `CloseButton`: secondary ink to match the row's meta text. */
const FILE_PREVIEW_REMOVE_STYLE: CSSProperties = {
	color: colors.text.secondary,
};

function collectValidFiles(files: FileList, existingCount: number): File[] {
	const maxBytes = MAX_ATTACHMENT_MB * 1024 * 1024;
	const valid: File[] = [];
	for (let i = 0; i < files.length; i++) {
		const file = files[i];
		if (!file) continue;
		if (file.size > maxBytes) continue;
		if (existingCount + valid.length >= MAX_ATTACHMENT_FILES) break;
		valid.push(file);
	}
	return valid;
}

function AttachButton({
	onFilesSelected,
	disabled,
	existingCount,
}: {
	onFilesSelected: (files: File[]) => void;
	disabled: boolean;
	existingCount: number;
}) {
	const inputRef = useRef<HTMLInputElement>(null);
	return (
		<div>
			<input
				ref={inputRef}
				type="file"
				multiple
				accept={ATTACHMENT_ACCEPT}
				className="hidden"
				aria-label={ATTACH_FILES_LABEL}
				onChange={(e) => {
					if (e.target.files) onFilesSelected(collectValidFiles(e.target.files, existingCount));
					if (inputRef.current) inputRef.current.value = '';
				}}
			/>
			<ButtonWithIcon
				variant="ghost"
				size="sm"
				icon="upload"
				onClick={() => inputRef.current?.click()}
				disabled={disabled}
				aria-label={ATTACH_FILES_LABEL}
				title={ATTACH_FILES_LABEL}
				style={ATTACH_BUTTON_STYLE}
			/>
		</div>
	);
}

function FilePreviewRow({ file, fileId, onRemove }: { file: SelectedFile; fileId: string; onRemove: (id: string) => void }) {
	const isImage = file.file.type.startsWith(IMAGE_MEDIA_TYPE_PREFIX);
	const statusText = file.uploading ? `Uploading... ${file.progress ?? 0}%` : formatFileSize(file.file.size);
	return (
		<div style={FILE_PREVIEW_ROW_STYLE}>
			<SemanticIcon name={isImage ? 'image' : 'file'} size="md" />
			<div className={LAYOUT_CLASSES.flexFillTruncate}>
				<div style={FILE_PREVIEW_NAME_STYLE}>{file.file.name}</div>
				<div style={FILE_PREVIEW_META_STYLE}>{statusText}</div>
			</div>
			{!file.uploading && (
				<CloseButton size="sm" onClick={() => onRemove(fileId)} aria-label={`Remove ${file.file.name}`} style={FILE_PREVIEW_REMOVE_STYLE} />
			)}
		</div>
	);
}

function FilePreviewList({ selectedFiles, onRemoveFile }: { selectedFiles: SelectedFile[]; onRemoveFile: (id: string) => void }) {
	if (selectedFiles.length === 0) return null;
	return (
		<div style={FILE_PREVIEW_LIST_STYLE}>
			{selectedFiles.map((fileItem) => (
				<FilePreviewRow key={fileItem.id} file={fileItem} fileId={fileItem.id} onRemove={onRemoveFile} />
			))}
		</div>
	);
}

/** The composer-local delta over a primary `ButtonWithIcon`: the square icon box the composer row is built around.
 *  Fill, ink and the unavailable treatment are the variant's, so the send button no longer carries its own
 *  can-send/cannot-send colour pair. */
const SEND_BUTTON_STYLE: CSSProperties = {
	borderRadius: borderRadius.lg,
	padding: spacing[3],
};

function SendErrorBanner({ error, onDismiss }: { error: string; onDismiss: () => void }) {
	return (
		<div className={LAYOUT_CLASSES.rowBetween} role={ARIA_ROLES.alert} aria-live={ARIA_LIVE.polite} style={SEND_ERROR_BANNER}>
			<span style={SEND_ERROR_TEXT}>{error}</span>
			<CloseButton size="sm" onClick={onDismiss} aria-label="Dismiss error" style={SEND_ERROR_DISMISS_BUTTON} />
		</div>
	);
}

/** "Reconnecting" is reserved for a socket that HAD opened: on a fresh panel the first connect is just connecting, and
 *  naming it a reconnect reported a failure the viewer never had. */
function getInputPlaceholder(hasConversation: boolean, wsConnected: boolean, everConnected: boolean): string {
	if (!hasConversation) return LOADING_TEXT;
	if (!wsConnected) return everConnected ? loadingMessages.reconnecting : loadingMessages.connecting;
	return 'Ask me anything...';
}

function MessageTextarea({
	inputRef,
	input,
	handleInputChange,
	handleKeyDown,
	hasConversation,
	wsConnected,
	everConnected,
}: {
	inputRef: React.RefObject<HTMLTextAreaElement | null>;
	input: string;
	handleInputChange: (e: React.ChangeEvent<HTMLTextAreaElement>) => void;
	handleKeyDown: (e: React.KeyboardEvent<HTMLTextAreaElement>) => void;
	hasConversation: boolean;
	wsConnected: boolean;
	everConnected: boolean;
}) {
	return (
		// `Textarea` owns the border, the focus ring, the transition and the disabled treatment, so the four utility
		// classes and the whole `TEXTAREA_FOCUS_HANDLERS` border-delta pair are gone rather than layered over it. What
		// stays is the composer's own geometry: it takes the free width in the row and does not grow.
		//
		// `flex-1` IS ON THE WRAPPER, NOT THE ELEMENT, and that distinction is the height bug. `Textarea` renders the
		// element inside its own field wrapper, which is a flex COLUMN -- so `flex-1` on the element resolved against
		// the VERTICAL axis: `flex-basis: 0%` outranked the `height` below when the main size was computed, and
		// `flex-grow: 1` then stretched the composer to fill the row. It never once took the free width it was written
		// for; the wrapper is already `width: 100%`. Sizing the wrapper is what puts the class on the axis it names.
		<Textarea
			ref={inputRef}
			value={input}
			onChange={handleInputChange}
			onKeyDown={handleKeyDown}
			placeholder={getInputPlaceholder(hasConversation, wsConnected, everConnected)}
			disabled={!hasConversation || !wsConnected}
			aria-label="Message input"
			containerClassName="flex-1"
			className="resize-none"
			style={MESSAGE_TEXTAREA}
		/>
	);
}

/**
 * The composer's send control.
 *
 * `loading` RATHER THAN A LABEL SWAP ALONE. An upload in flight already forces `canSend` false
 * (`AssistantPane.hooks.effects.ts`), so the button was disabled and said "Uploading..." while looking identical to a
 * button the user simply may not press. `loading` disables, swaps the submit glyph for the spinner AND writes
 * `aria-busy`, which is the half the hand-rolled version never had.
 */
function SendButton({ onClick, canSend, isUploading }: { onClick: () => void; canSend: boolean; isUploading: boolean }) {
	return (
		<ButtonWithIcon
			variant="primary"
			size="sm"
			icon="submit"
			loading={isUploading}
			onClick={onClick}
			disabled={!canSend}
			style={SEND_BUTTON_STYLE}
			aria-label={isUploading ? 'Uploading...' : 'Send message'}
		/>
	);
}

function InputHint({ fileCount }: { fileCount: number }) {
	if (fileCount === 0) return null;
	return <div style={INPUT_HINT_STYLE}>{countNoun(fileCount, 'file')} attached</div>;
}

interface InputAreaProps {
	sendError: string | null;
	setSendError: (error: string | null) => void;
	selectedFiles: SelectedFile[];
	handleFilesSelected: (files: File[]) => void;
	handleRemoveFile: (fileId: string) => void;
	isUploading: boolean;
	uploadProgress: number;
	hasConversation: boolean;
	inputRef: React.RefObject<HTMLTextAreaElement | null>;
	input: string;
	handleInputChange: (e: React.ChangeEvent<HTMLTextAreaElement>) => void;
	handleKeyDown: (e: React.KeyboardEvent<HTMLTextAreaElement>) => void;
	wsConnected: boolean;
	everConnected: boolean;
	handleSendMessage: () => void;
	canSend: boolean;
}

export function InputArea({
	sendError,
	setSendError,
	selectedFiles,
	handleFilesSelected,
	handleRemoveFile,
	isUploading,
	uploadProgress,
	hasConversation,
	inputRef,
	input,
	handleInputChange,
	handleKeyDown,
	wsConnected,
	everConnected,
	handleSendMessage,
	canSend,
}: InputAreaProps) {
	return (
		<div className="border-t" style={INPUT_AREA_CONTAINER}>
			{sendError ? <SendErrorBanner error={sendError} onDismiss={() => setSendError(null)} /> : null}
			<FilePreviewList selectedFiles={selectedFiles} onRemoveFile={handleRemoveFile} />
			{isUploading ? <UploadProgressBar progress={uploadProgress} /> : null}
			<div className={LAYOUT_CLASSES.row} style={INPUT_ROW_GAP}>
				<AttachButton onFilesSelected={handleFilesSelected} disabled={!hasConversation} existingCount={selectedFiles.length} />
				<MessageTextarea
					inputRef={inputRef}
					input={input}
					handleInputChange={handleInputChange}
					handleKeyDown={handleKeyDown}
					hasConversation={hasConversation}
					wsConnected={wsConnected}
					everConnected={everConnected}
				/>
				<SendButton onClick={handleSendMessage} canSend={canSend} isUploading={isUploading} />
			</div>
			<InputHint fileCount={selectedFiles.length} />
		</div>
	);
}
